/**
 * Beacon Golf — last-viewed-course tracker (BEACON-55 D.2)
 *
 * Tiny IndexedDB key/value store that records the most recently opened
 * course. The /offline.html page reads this key (without importing any
 * modules — see offline.html inline script) so the offline state can offer
 * a useful next step ("Open <last-viewed-course> brief") instead of just
 * dumping the user at /.
 *
 * Schema:
 *   db: "beacon-golf" v1
 *   store: "kv"
 *   key: "last-viewed-course"
 *   value: { slug, name, viewedAt }   // viewedAt = epoch ms
 *
 * The store is shared with any other small KV the PWA accumulates over time.
 * Keep payloads tiny; use Cache Storage / dedicated stores for larger blobs.
 */

// CCQ-430 R1 M-4 fold: SCHEMA-PINNED-TO-OFFLINE-HTML.
// If you bump DB_VERSION here, you MUST also:
//   1. Open `pwa/public/offline.html` (which intentionally opens the DB
//      unversioned now per M-4 fold), confirm the inline script's read path
//      still works against the new schema, and
//   2. Bump CACHE_VERSION in `pwa/public/sw.js` so the precached offline.html
//      is replaced on next deploy (otherwise stale precaches keep the old
//      schema assumptions in flight).
// Tracking ticket for promotion to a build-time shared schema constant: post-W2.
const DB_NAME = "beacon-golf";
const DB_VERSION = 1;
const STORE = "kv";
const KEY_LAST_VIEWED = "last-viewed-course";

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function writeKV(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    // CCQ-430 R1 L-4 fold: resolve on transaction completion, not request
    // success. IndexedDB fires `request.onsuccess` BEFORE the transaction
    // commits; if the tx subsequently aborts (quota, schema rollback,
    // page-unload mid-flush), we'd report success on data that never
    // persisted. Single-key overwrite keeps worst-case impact minimal
    // (stale offline deep link), but the contract should be honest.
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Record that the user is currently viewing `slug`. Best-effort; logs and
 * swallows errors so a flaky IndexedDB doesn't break the brief panel.
 *
 * @param {string} slug - lowercase kebab-case course slug
 * @param {string} name - display name (e.g. "Bandon Dunes")
 */
export async function recordLastViewed(slug, name) {
  if (!slug || !name) return;
  try {
    await writeKV(KEY_LAST_VIEWED, {
      slug,
      name,
      viewedAt: Date.now(),
    });
  } catch (err) {
    console.warn("[last-viewed] write failed:", err);
  }
}
